import { QueryClient } from '@tanstack/react-query'
import { bumpIdentityGeneration, setIdentityStale } from '@/services/api/identityEpoch'
import { sweepIdentityScopedStorage } from './identityStorage'

/**
 * A 403/404 is a definitive answer about THIS identity+tenant (authorization
 * or "not in your organization"); repeating the request cannot change it, it
 * only delays the error state and, for 404, fires a pointless duplicate
 * request. Duck-typed on `status` (apiClient throws plain ApiError objects)
 * rather than importing services/api/client, which would couple this module
 * to the auth stack that imports it.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status
  if (status === 403 || status === 404) return false
  // A request dropped because the identity changed must never be replayed.
  if ((error as { code?: unknown } | null)?.code === 'stale_identity') return false
  return failureCount < 1
}

/**
 * Shared QueryClient singleton.
 *
 * Exported here so class components (e.g. RouteErrorBoundary) and
 * non-hook contexts can access the client without importing from main.tsx,
 * which would cause a circular dependency.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery,
    },
  },
})

export type ResetReason = 'login' | 'logout' | 'tenant'

const resetHooks = new Map<string, (reason: ResetReason) => void>()
let anonymousHooks = 0

/**
 * Register extra client-side state that is scoped to one identity/tenant
 * (in-memory Zustand stores, sockets) so resetClientState() clears it too.
 * Registration is a module side effect of the owning store, so this module
 * never imports those stores (no cycles). Returns an unregister function.
 */
export function registerClientStateReset(fn: (reason: ResetReason) => void, key?: string): () => void {
  // Keyed so an HMR re-evaluation of the owning module REPLACES its hook
  // instead of stacking a stale one that targets a dead store instance.
  const k = key ?? `anon-${anonymousHooks++}`
  resetHooks.set(k, fn)
  return () => {
    if (resetHooks.get(k) === fn) resetHooks.delete(k)
  }
}

/**
 * Steps 1-3 and 5 of the reset: new identity epoch (aborts every request or
 * stream issued under the old identity; late responses are dropped by apiClient
 * as StaleIdentityError and their 401s never reach the forced-logout path),
 * cancel, and drop every cached query and mutation. Also used on its own by the
 * cross-tab guard, which must not touch shared storage or sessions.
 *
 * cancelQueries is fire-and-forget on purpose: awaiting would open an async gap
 * between aborting the old identity and the caller swapping the token. clear()
 * destroys each query (a silent cancel) in the same tick, which is what
 * actually guarantees no repopulation.
 */
export function resetQueryState(): void {
  bumpIdentityGeneration()
  void queryClient.cancelQueries()
  queryClient.clear()
}

/**
 * THE chokepoint (issue #301). authStore calls this on every identity or
 * tenant change: login, selectTenant, switchTenant, logout and every forced
 * logout (they all funnel through logout()).
 *
 * QueryClient.clear() removes every query and mutation and destroys each
 * query, which cancels its retryer silently: a response that arrives late
 * from a request sent under the OLD token is dropped instead of repopulating
 * the cache. Still-mounted observers re-create their query and refetch under
 * the new token.
 */
export function resetClientState(reason: ResetReason): void {
  resetQueryState()
  // This tab itself changed identity: any stale flag from a cross-tab change
  // no longer applies.
  setIdentityStale(false)
  // 6. Tenant-scoped stores / sockets register here.
  resetHooks.forEach((fn) => {
    try {
      fn(reason)
    } catch (err) {
      console.error('resetClientState hook failed:', err)
    }
  })
  // 7. Identity-scoped drafts / acknowledgements.
  sweepIdentityScopedStorage()
}
